import { createConsumer, Consumer, Subscription } from '@rails/actioncable';
import { WebSocketMessage } from '../types';
import { authService } from './auth';

type WebSocketMessageHandler = (data: WebSocketMessage) => void;

class WebSocketService {
  private consumer: Consumer | null = null;
  private subscription: Subscription | null = null;
  private messageHandlers: Set<WebSocketMessageHandler> = new Set();

  private get webSocketUrl(): string {
    const baseUrl =
      process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    // Remove /api/v1 from the base URL for WebSocket connection since ActionCable is mounted at /cable
    const wsBaseUrl = baseUrl.replace(/\/api\/v1$/, '').replace(/^http/, 'ws');
    return wsBaseUrl + '/cable';
  }

  connect(): void {
    if (this.consumer) {
      console.log('WebSocket already connected');
      return;
    }

    const token = authService.getAuthToken();
    if (!token) {
      console.warn('No auth token available for WebSocket connection');
      return;
    }

    try {
      this.consumer = createConsumer(
        `${this.webSocketUrl}?token=${encodeURIComponent(token)}`
      );
      this.subscribeToAudioFileChannel();
      console.log('WebSocket connected');
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }

  disconnect(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }

    this.messageHandlers.clear();
    console.log('WebSocket disconnected');
  }

  private subscribeToAudioFileChannel(): void {
    if (!this.consumer) return;

    this.subscription = this.consumer.subscriptions.create('AudioFileChannel', {
      connected: () => {
        console.log('Connected to AudioFileChannel');
      },

      disconnected: () => {
        console.log('Disconnected from AudioFileChannel');
      },

      received: (data: WebSocketMessage) => {
        console.log('WebSocket message received:', data);
        this.notifyHandlers(data);
      },

      rejected: () => {
        console.error('AudioFileChannel subscription rejected');
      },
    });
  }

  addMessageHandler(handler: WebSocketMessageHandler): void {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler: WebSocketMessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  private notifyHandlers(data: WebSocketMessage): void {
    this.messageHandlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in WebSocket message handler:', error);
      }
    });
  }

  isConnected(): boolean {
    return this.consumer !== null && this.subscription !== null;
  }

  // Reconnect method for handling connection drops
  reconnect(): void {
    this.disconnect();
    setTimeout(() => {
      this.connect();
    }, 1000);
  }
}

export const webSocketService = new WebSocketService();
